import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import {
    fullScreenLoader,
    removeFullScreenLoader,
} from "@/components/jsComponents";
import { user } from "@/code/user";
import { applySeo } from "@/code/seo";
import { seoForRoute } from "@/code/seo-meta";
import { isChunkLoadError, reloadOnto } from "@/code/chunk-reload";
import { newBuildIsLive, startNewBuildWatch } from "@/code/new-build";
import { showToast } from "@/components/toast";
import { MCP_BASE_URL } from "@/code/mcp_oauth";

import Main from "@/views/main.vue";
import ServiceMain from "@/views/service/main.vue";
import LandingPage from "@/views/landing.vue";
import Login from "@/views/login.vue";
import Signup from "@/views/signup.vue";
import NotFound from "@/views/not-found.vue";
import ProjectList from "@/views/project-list.vue";
// Project Settings is the landing child of /my-projects/:service, so it is
// what renders the moment a project is opened. Loaded statically, not with
// import(), so entering a project does not wait on a second network round
// trip for its chunk.
import ServiceSettings from "@/views/service/service.vue";

let checkUser = async (t: any, f: any, n: any) => {
    let u = user?.user_id;
    if (u) {
        return n();
    } else {
        // A deep link into the dashboard (the MCP server and the docs hand out
        // /my-projects?job=create_project) survives the sign-in: the login page
        // returns to ?redirect once the visitor is in. Only a path, never a
        // host, and only when it is not the default landing page anyway.
        const wanted = typeof t?.fullPath === "string" ? t.fullPath : "";
        if (wanted && wanted !== "/my-projects" && wanted.startsWith("/") && !wanted.startsWith("//")) {
            n({ path: "/login", query: { redirect: wanted } });
            return;
        }
        // // subscription 페이지로 바로 접근 했는데 로그인이 안된 상태라면 로그인 페이지로 보내고, 로그인 후에 subscription 페이지로 돌아오게 함
        // if (t.name === "subscription" || t.name === "subscription-url") {
        //     let redirectPath = t.name ? t.name : "";
        //     let serviceId = t.params.service ? t.params.service : "";
        //     n(`/login?redirect=true&rdp=${redirectPath}&sid=${serviceId}`);
        //     return;
        // }
    }
    n("/login");
};

const router = createRouter({
    scrollBehavior(to, from) {
        // A change of query alone is not a new page. The settings page drops
        // its ?focus once it has scrolled to the card it was sent to, and
        // treating that as a navigation would throw the page back to the top.
        if (to.path === from.path) return false;
        // Anything else starts at the top.
        return { top: 0 };
    },
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: "/login",
            name: "login",
            component: Login,
        },
        {
            path: "/signup",
            name: "signup",
            component: Signup,
        },
        {
            path: "/signup-confirmation",
            name: "confirmation",
            component: () => import("@/views/signup-confirmation.vue"),
        },
        {
            path: "/email-verification",
            name: "verification",
            component: () => import("@/views/account-email-verification.vue"),
        },
        {
            path: "/forgot-password",
            name: "forgot",
            component: () => import("@/views/account-forgot-password.vue"),
        },
        {
            path: "/change-password",
            name: "change-password",
            component: () => import("@/views/account-change-password.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/delete-account",
            name: "delete-account",
            component: () => import("@/views/account-delete.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        // {
        //     path: "/signup_confirmed_template",
        //     name: "signup_confirmed",
        //     component: () => import("@/views/email_confirm_templates/signup_confirmed_template.vue"),
        // },
        // {
        //     path: "/invitation_confirmed_template",
        //     name: "invitation_confirmed",
        //     component: () =>
        //         import("@/views/email_confirm_templates/invitation_confirmed_template.vue"),
        // },
        {
            path: "/enable-account/:email",
            name: "enable-account",
            component: () => import("@/views/account-enable.vue"),
        },
        {
            path: "/signup-success",
            name: "success",
            component: () => import("@/views/signup-success.vue"),
        },
        {
            path: "/bye",
            name: "bye",
            component: () => import("@/views/bye.vue"),
        },
        {
            // The project form on its own page: where an account with no project
            // lands (the empty list sends it here) and where the MCP server and
            // the documentation send someone who has to create a project.
            path: "/new-project",
            name: "new-project",
            component: () => import("@/views/project-new.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            // Step two of a new project: the plan (views/new-project.ts).
            path: "/create",
            name: "create",
            component: () => import("@/views/project-plan.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/delete-service/:id",
            name: "delete-service",
            component: () => import("@/views/project-delete.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            path: "/subscription/:service",
            name: "subscription",
            component: () => import("@/views/subscription/subscription.vue"),
            meta: { requiresAuth: true },
            beforeEnter: checkUser,
        },
        {
            // pages with navbar
            path: "/",
            component: Main,
            children: [
                {
                    path: "/",
                    name: "home",
                    component: LandingPage,
                },
                {
                    path: "articles",
                    name: "articles",
                    component: () => import("@/views/articles.vue"),
                },
                {
                    path: "articles/:slug",
                    name: "article",
                    component: () => import("@/views/article.vue"),
                },
                {
                    path: "account-settings",
                    name: "account-settings",
                    component: () => import("@/views/account-settings.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                },
                {
                    path: "my-projects",
                    name: "my-projects",
                    component: ProjectList,
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                },
                {
                    path: "my-projects/:service",
                    component: ServiceMain,
                    meta: { requiresAuth: true },
                    beforeEnter: checkUser,
                    children: [
                        {
                            path: "",
                            name: "dashboard",
                            component: ServiceSettings,
                        },
                        {
                            path: "users",
                            name: "users",
                            component: () =>
                                import("@/views/service/users/users.vue"),
                        },
                        {
                            path: "records",
                            name: "records",
                            component: () =>
                                import("@/views/service/records/records.vue"),
                        },
                        {
                            path: "openid",
                            name: "openid",
                            component: () =>
                                import("@/views/service/openid/openid.vue"),
                        },
                        {
                            path: "tickets",
                            name: "tickets",
                            component: () =>
                                import("@/views/service/tickets/tickets.vue"),
                        },
                        {
                            path: "mail",
                            name: "mail",
                            component: () =>
                                import("@/views/service/mail/mail.vue"),
                        },
                        {
                            path: "newsletter",
                            name: "newsletter",
                            component: () =>
                                import("@/views/service/newsletter/newsletter.vue"),
                        },
                        {
                            path: "hosting",
                            name: "hosting",
                            component: () =>
                                import("@/views/service/hosting/hosting.vue"),
                        },
                        {
                            path: "clientsecret",
                            name: "clientsecret",
                            component: () =>
                                import("@/views/service/clientsecret/clientsecret.vue"),
                        },
                    ],
                },
            ],
        },
        // Paths of the previous www.skapi.com app, which this site replaces on
        // the same host. Bookmarks, old e-mails, search results and payment
        // return URLs still point at them, so each lands on its successor
        // instead of on the noindex "Page not found". Query and hash carry
        // over (vue-router keeps them on a redirect that names no query of
        // its own), so a ?code= or ?email= still reaches the new page. These
        // are client-side redirects: a true 301 needs a CloudFront Function.
        { path: "/use-cases", redirect: "/" },
        {
            // skapi.com/mcp: sign in to the Skapi MCP server from a terminal, a script or a
            // platform that only takes a header. The MCP server's /connect page runs the
            // sign-in through /login and issues the access token; this route only sends the
            // browser there, so the address people type is on skapi.com. The component is
            // never rendered: the guard leaves the app.
            path: "/mcp",
            name: "mcp-connect",
            component: NotFound,
            beforeEnter: () => {
                window.location.assign(`${MCP_BASE_URL}/connect`);
                return false;
            },
        },
        { path: "/refer/:name", redirect: "/signup" },
        { path: "/oauth-login", redirect: "/login" },
        { path: "/forgot", redirect: "/forgot-password" },
        { path: "/confirmation", redirect: "/signup-confirmation" },
        { path: "/verification", redirect: "/email-verification" },
        { path: "/success/:how?", redirect: "/signup-success" },
        { path: "/account-setting", redirect: "/account-settings" },
        { path: "/my-services", redirect: "/my-projects" },
        {
            path: "/my-services/:service/dashboard",
            redirect: (to) => ({ path: `/my-projects/${to.params.service}` }),
        },
        {
            path: "/my-services/:service/:rest(.*)*",
            redirect: (to) => {
                const rest = ([] as string[]).concat(to.params.rest || []).join("/");
                return { path: `/my-projects/${to.params.service}${rest ? `/${rest}` : ""}` };
            },
        },
        {
            path: "/subscription/url/:service",
            redirect: (to) => ({ path: `/subscription/${to.params.service}` }),
        },
        // 404 page
        {
            path: "/404page",
            name: "404page",
            component: NotFound,
        },
        {
            path: "/:pathMatch(.*)*",
            redirect: "/404page",
        },
    ],
});

// 깜빡임 방지용 타이머
let loadingTimer: number | null = null;

// A deploy since this tab was loaded is noticed in the background (code/new-build.ts) and
// taken up at the next navigation below: the site loads again onto the page asked for.
startNewBuildWatch();

router.beforeEach(async (to, from, next) => {
    // Clear any previous timer to prevent stale loader from firing after a redirect chain
    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Not on the first navigation of a load (nothing to catch up on yet, and the build was
    // just fetched). reloadOnto() refuses a page this load was itself started for.
    if (from.matched.length && newBuildIsLive() && reloadOnto(router.resolve(to.fullPath).href)) {
        return next(false);
    }

    // // 너무 짧은 이동은 로딩 안 보이게 150ms 지연
    // loadingTimer = window.setTimeout(() => {
    //     fullScreenLoader();
    // }, 150);

    // Show loader only when the route path actually changes (not for query-only changes)
    if (to.path !== from.path || from.name === undefined) {
        fullScreenLoader();
    }
    next();
});

router.afterEach((to) => {
    // Rewrite title / description / canonical / social card / JSON-LD for the
    // page we just landed on. Every route shares one static head in index.html,
    // so without this the whole site claims to be `/`: see seo-meta.ts. The
    // article detail view calls applySeo() a second time once its metadata is
    // loaded, to swap in the real title and BlogPosting node.
    applySeo(seoForRoute(String(to.name ?? ""), to.path));

    // Page views are reported by src/code/analytics.ts.

    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Drop the full-screen overlay as soon as the destination view is in the
    // DOM. Data loading is each page's own job (section skeletons/loaders), so
    // the whole screen is never blocked on a fetch. Views still calling
    // removeFullScreenLoader() later are harmless no-ops. During an OAuth
    // return (?code=...) keep the overlay up; that flow manages it itself.
    const urlParams = new URLSearchParams(window.location.search);
    const isProjectRoute = to.path.startsWith("/my-projects/");
    if (!urlParams.get("code") && !isProjectRoute && to.name !== "my-projects") {
        nextTick(() => removeFullScreenLoader());
    }
});

// 에러 발생 시에도 로딩 해제
router.onError((error, to) => {
    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // The files of the page did not load (code/chunk-reload.ts). Load the site
    // again, onto that page; the loader stays up until the browser has left
    // this one. If that was tried already, the user is told.
    if (isChunkLoadError(error)) {
        if (reloadOnto(router.resolve(to.fullPath).href)) {
            // Back can bring this page out of the browser's back/forward
            // cache as it was left, with the loader over it.
            window.addEventListener(
                "pageshow",
                (e) => e.persisted && removeFullScreenLoader(),
                { once: true },
            );
            return;
        }
        showToast("This page could not be loaded. Check your connection and try again.");
    }

    // Google OAuth 처리 중이 아닐 때만 로더 제거
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get("code")) {
        removeFullScreenLoader();
    }
});

export default router;
